import { Box } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getErrorByPath } from '../../../../app/utils'
import SharedPhysicalProperties from '../../../../components/Forms/PhysicalProperties/SharedPhysicalProperties'
import TabWrapper from '../../../../components/TabWrapper/TabWrapper'
import { useOrderFormContext } from '../../context/OrderFormContext'
import BatchDetails from './TabContents/BatchDetails'
import HSCodesTab from './TabContents/HSCodesTab'
import TemperatureTab from './TabContents/TemperatureTab'

interface TabItemProps {
  label: string
  value: string
  component: React.FC<{ prefix?: string; variant?: string }>
  conditionalShow?: () => boolean
  hasError?: boolean
}

interface LoadContentTabsProps {
  bookingIndex?: number
  prefix?: string
  disableAllExceptBatch?: boolean
}

const LoadContentTabs: React.FC<LoadContentTabsProps> = ({ prefix = '', bookingIndex, disableAllExceptBatch = false }) => {
  const { watch, formState: { errors } } = useFormContext()
  const [activeTab, setActiveTab] = useState<number>(0)

  const { variant } = useOrderFormContext()

  const isClearingRequired = useMemo(() => {
    const booking = watch(`bookings.${bookingIndex ?? -1}`)
    if (!booking) return false
    const result =
      booking.waypoints.some((waypoint: any) => waypoint.clearingRequired) ||
      booking.deliverTo.clearingRequired ||
      booking.shipFrom.clearingRequired
    return result
  }, [watch, bookingIndex])


  const packagingData = watch(prefix)

  const { temperatureSetting } = packagingData



  // Debug: Log the errors to see the actual structure
  console.log('LoadContentTabs errors:', errors)
  console.log('LoadContentTabs prefix:', prefix)

  const hasTemperatureErrors = !!getErrorByPath(errors, prefix ? `${prefix}.temperatureSetting` : 'temperatureSetting')
  const hasPhysicalPropertiesErrors = !!getErrorByPath(errors, prefix ? `${prefix}.physicalProperties` : 'physicalProperties')
  const hasHSCodesErrors = !!getErrorByPath(errors, prefix ? `${prefix}.hsHeadingId` : 'hsHeadingId') || !!getErrorByPath(errors, prefix ? `${prefix}.hsSubheadingId` : 'hsSubheadingId')


  const PhysicalPropertiesTab = useMemo(() => {
    const Component = () => (
      <Box sx={{ padding: '22px 16px 0 16px' }}>
        <SharedPhysicalProperties prefix={prefix} isVolume={true} variant={variant === 'summary' ? 'contentSummary' : 'product'} isDisabled={disableAllExceptBatch} />
      </Box>
    )
    Component.displayName = 'PhysicalPropertiesTab'
    return Component
  }, [prefix, variant, disableAllExceptBatch])

  const TemperatureTabComponent = useMemo(() => {
    const Component = (props: any) => <TemperatureTab {...props} disabled={disableAllExceptBatch} />
    Component.displayName = 'TemperatureTabComponent'
    return Component
  }, [disableAllExceptBatch])

  const HSCodesTabComponent = useMemo(() => {
    const Component = (props: any) => <HSCodesTab {...props} disabled={disableAllExceptBatch} />
    Component.displayName = 'HSCodesTabComponent'
    return Component
  }, [disableAllExceptBatch])

  const TABS: TabItemProps[] = useMemo(() => [
    { label: 'BATCH DETAILS', value: 'batch-details', component: BatchDetails },
    {
      label: 'TEMPERATURE',
      value: 'temperature',
      component: TemperatureTabComponent,
      hasError: hasTemperatureErrors
    },
    {
      label: 'PHYSICAL PROPERTIES',
      value: 'physical-properties',
      component: PhysicalPropertiesTab,
      hasError: hasPhysicalPropertiesErrors,
    },
    {
      label: 'HS CODES',
      value: 'hs-codes',
      component: HSCodesTabComponent,
      hasError: hasHSCodesErrors,
    },
  ], [TemperatureTabComponent, PhysicalPropertiesTab, HSCodesTabComponent, hasTemperatureErrors, hasPhysicalPropertiesErrors, hasHSCodesErrors])

  const visibleTabs = useMemo(() => {
    return TABS.filter((tab) => !tab.conditionalShow || tab.conditionalShow())
  }, [TABS, isClearingRequired, disableAllExceptBatch])

  const tabWrapperTabs = useMemo(() => visibleTabs.map((tab, idx) => ({
    label: tab.label,
    content: <tab.component prefix={prefix} variant={variant} />, // already handled disabled in component
    withBorder: true,
    hasError: tab.hasError,
  })), [visibleTabs, prefix, variant])

  useEffect(() => {
    if (activeTab >= tabWrapperTabs.length) {
      setActiveTab(0)
    }
  }, [tabWrapperTabs.length, activeTab])

  return (
    <Box>
      <TabWrapper
        tabs={tabWrapperTabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Load Content Tabs"
      />
    </Box>
  )
}

export default LoadContentTabs
